import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { useCurrency } from "@/context/CurrencyContext";
import { ShieldCheck, Plane, ArrowRight, Sparkles } from "lucide-react";
import SEOHead from "@/components/app/SEOHead";
import { courseTuitionUsd } from "@/lib/courseTuition";

// STEM OPT pathway buckets. STEM OPT is a US-immigration extension that
// lets F-1 grads work 3 years post-graduation on eligible STEM degrees
// (vs 1 year for non-STEM). Every row here carries the same
// `international.shortage_occupation: true` flag — grouping is
// presentational so international families can spot the relevant
// vertical fast.
const PATHWAYS = [
  {
    key: "tech-ai",
    label: "Tech & AI",
    match: (d) => /artificial|data|comput|software|cyber|robotic/i.test(d.official_title),
    blurb: "AI, Data Science, Software Engineering and Cybersecurity are the fastest STEM-OPT-to-H-1B pipeline. Big Tech (Google, Meta, Amazon) files thousands of H-1Bs a year for these grads.",
  },
  {
    key: "engineering",
    label: "Engineering",
    match: (d) => /engineer/i.test(d.official_title),
    blurb: "Civil, Mechanical, Aerospace and Electrical Engineering all qualify for the STEM OPT extension — plus employers like Boeing, SpaceX and Lockheed have long H-1B track records.",
  },
  {
    key: "healthcare",
    label: "Healthcare & Life Sciences",
    match: (d) => /nurs|health|forensic|biochem|geneti|neurosci|nutrit/i.test(d.official_title),
    blurb: "Nursing sits on every US state's shortage list, and STEM-designated life-science degrees route directly to Genentech, Regeneron, Moderna and the NIH.",
  },
  {
    key: "business-analytics",
    label: "Business Analytics",
    match: (d) => /analyt/i.test(d.official_title),
    blurb: "STEM-designated Business Analytics (AACSB + STEM-CIP code) unlocks a 3-year OPT on a business degree — a game-changer for consulting and finance recruits.",
  },
  {
    key: "other",
    label: "Other STEM-designated pathways",
    match: () => true,
    blurb: "Additional STEM-designated undergraduate paths eligible for the OPT extension.",
  },
];

function DegreeCard({ deg, uni, format }) {
  const offer = deg?.admissions?.offer_rate;
  const tuition = courseTuitionUsd(deg);
  const salary = deg?.outcomes?.median_starting_salary_usd;
  const psw = deg?.international?.psw_eligible_years;
  const accred = (deg?.accreditation || [])
    .map((a) => a?.body)
    .filter((b) => b && b !== "Institutional accreditor")
    .slice(0, 3);
  return (
    <Link
      to={`/degree/${deg.id}`}
      data-testid={`stem-opt-degree-card-${deg.id}`}
      className="group border border-stone-200 rounded-lg p-5 bg-white hover:border-stone-500 transition-colors block"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label-uppercase !text-[10px] text-stone-500">{uni?.name}</div>
          <div className="font-editorial text-lg mt-1 group-hover:underline underline-offset-4 decoration-stone-400">
            {deg.official_title}
          </div>
          <div className="text-xs text-stone-500 mt-1 font-mono">
            {deg.qualification} · {deg.duration_years} yr · {uni?.city}
          </div>
        </div>
        {typeof offer === "number" && (
          <div className="text-right shrink-0">
            <div className="label-uppercase !text-[9px] text-stone-500">Admit rate</div>
            <div className="font-mono text-lg mt-0.5">{Math.round(offer * 100)}%</div>
          </div>
        )}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
        <div>
          <div className="label-uppercase !text-[9px] text-stone-500">Intl tuition</div>
          <div className="font-mono mt-0.5">{tuition ? format(tuition) : "—"}</div>
        </div>
        <div>
          <div className="label-uppercase !text-[9px] text-stone-500">Starting salary</div>
          <div className="font-mono mt-0.5">{salary ? format(salary) : "—"}</div>
        </div>
        <div>
          <div className="label-uppercase !text-[9px] text-stone-500">STEM OPT</div>
          <div className="font-mono mt-0.5">{psw ? `${psw} yr` : "—"}</div>
        </div>
      </div>

      {accred.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {accred.map((body) => (
            <Badge key={body} variant="secondary" className="text-[10px] font-normal">
              {body}
            </Badge>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center gap-1.5 text-xs text-emerald-700">
        <ShieldCheck className="w-3.5 h-3.5" />
        <span>STEM-designated CIP code — eligible for the 3-year OPT extension.</span>
      </div>
    </Link>
  );
}


export default function UsStemOptDegrees() {
  const [data, setData] = useState({ degrees: [], universities: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const { format } = useCurrency();

  useEffect(() => {
    api.get("/lists/us-stem-opt-degrees")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const claimed = new Set();
    const out = [];
    for (const p of PATHWAYS) {
      const rows = data.degrees.filter((d) => {
        if (claimed.has(d.id)) return false;
        if (p.key === "other") { claimed.add(d.id); return true; }
        const ok = p.match(d);
        if (ok) claimed.add(d.id);
        return ok;
      });
      if (rows.length) out.push({ ...p, rows });
    }
    return out;
  }, [data]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <SEOHead
        title="US STEM OPT-qualifying degrees · WhichUniversityAdvisor"
        description="Editorially-verified US degrees that qualify for the 36-month STEM OPT extension — the longest post-study work window in the US. Computer Science, Data Science, Biomedical, Engineering and more."
        image="/og/us-stem-opt-degrees.png"
        path="/us-stem-opt-degrees"
      />
      {/* HERO */}
      <div className="grid md:grid-cols-3 gap-8 items-start" data-testid="stem-opt-hero">
        <div className="md:col-span-2">
          <div className="label-uppercase text-stone-500">The visa-first shortlist · US edition</div>
          <h1 className="font-editorial text-4xl sm:text-5xl mt-3 leading-tight">
            Which US degree gives you the <em>longest</em> work runway after graduation?
          </h1>
          <p className="mt-5 text-stone-700 max-w-2xl leading-relaxed">
            The US federal <span className="font-semibold">STEM OPT extension</span> lets F-1 international
            students work for <span className="font-semibold">three years</span> after graduation on
            eligible STEM-designated degrees — a full <span className="font-semibold">2 extra years</span>
            over non-STEM programmes. That's the longest legal H-1B runway in the country. Every
            degree below is STEM-designated (or on a US shortage list), and each entry is
            editorially verified against the university's programme page.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs">
            <div className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
              <Plane className="w-3.5 h-3.5 text-stone-600" />
              <span className="font-underground-sc tracking-wider">3-yr OPT → cap-exempt H-1B lottery entries</span>
            </div>
            <div className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
              <span className="font-underground-sc tracking-wider">Verified against official programme pages</span>
            </div>
          </div>
        </div>

        <aside
          data-testid="stem-opt-counter"
          className="rounded-lg border border-stone-300 bg-[#F3F1EC] p-6"
        >
          <div className="label-uppercase !text-[10px] text-stone-500">Currently on this list</div>
          <div className="font-editorial text-5xl mt-2">{data.total}</div>
          <div className="text-sm text-stone-600 mt-1">
            editorial degrees across <span className="font-semibold">{Object.keys(data.universities).length}</span> US universities
          </div>
          <div className="mt-5 flex items-center gap-2 text-xs text-stone-500">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Sorted by admit rate — easiest offers first.</span>
          </div>
        </aside>
      </div>

      {/* EDITORIAL NOTE */}
      <div className="mt-10 border-l-2 border-stone-400 pl-4 py-2 text-sm text-stone-600 max-w-3xl italic"
           data-testid="stem-opt-editorial-note">
        Editorial note: This list is informational and reflects the current
        US ICE STEM-designated Degree Program List (DHS) + widely-published
        shortage occupations. Individual CIP-code eligibility can change
        with DHS updates — always confirm with your Designated School Official
        before applying. WhichUniversityAdvisor doesn't provide legal advice.
      </div>

      {/* GROUPED PATHWAYS */}
      {!loading && grouped.map((g) => (
        <section key={g.key} className="mt-14" data-testid={`stem-opt-section-${g.key}`}>
          <div className="flex items-baseline justify-between border-b border-stone-200 pb-3">
            <div>
              <div className="label-uppercase text-stone-500">{g.rows.length} pathway{g.rows.length === 1 ? "" : "s"}</div>
              <h2 className="font-editorial text-2xl sm:text-3xl mt-1">{g.label}</h2>
            </div>
          </div>
          <p className="text-sm text-stone-600 mt-4 max-w-3xl">{g.blurb}</p>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {g.rows.map((d) => (
              <DegreeCard
                key={d.id}
                deg={d}
                uni={data.universities[d.university_id]}
                format={format}
              />
            ))}
          </div>
        </section>
      ))}

      {/* FOOTER CTA */}
      <div className="mt-20 rounded-lg border border-stone-300 bg-white p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="label-uppercase text-stone-500">Not sure which one fits your grades?</div>
          <h3 className="font-editorial text-2xl mt-1">Run Advanced Search with your real profile.</h3>
          <p className="text-sm text-stone-600 mt-2 max-w-xl">
            We'll score every one of these against your GPA, SAT/ACT, budget, and passport — and show only the ones you can actually get into.
          </p>
        </div>
        <Link
          to="/advanced"
          data-testid="stem-opt-cta-advanced"
          className="inline-flex items-center gap-2 bg-[#002147] hover:bg-[#001A38] text-white px-5 py-3 rounded-md text-sm"
        >
          Open Advanced Search <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
